import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import store from '../store/store';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import {
  collectProcessFormatRecord,
  savesectionApicall,
  fetchCycleData,
  type ProcessFormatCycleData as FetchedCycleData,
} from '../Services/ProcessFormatRecord';
import type { ProcessFormatCycleData, ProcessFormatItemData } from '../store/ProcessFormatSlice';
import { addOfflineData, setFetchedCycles } from '../store/ProcessFormatSlice';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';
import moment from 'moment';
import {
  CycleHeader,
  CycleCompletedHeader,
  CycleSavedBanner,
  StartSessionButton,
} from '../shared/components/cycle';

const PROCESS_FORMAT_INITIAL_KEY = 'processFormatInitialData';
const PROCESS_FORMAT_CYCLE_DATA_KEY = 'process-format-cycle';

interface ProcessFormatInitialData {
  productName: string;
  executiveName: string;
  lineNo: string;
}

function getProcessFormatInitial(): ProcessFormatInitialData | null {
  try {
    const s = localStorage.getItem(PROCESS_FORMAT_INITIAL_KEY);
    return s ? (JSON.parse(s) as ProcessFormatInitialData) : null;
  } catch {
    return null;
  }
}

function saveProcessFormatInitial(data: ProcessFormatInitialData): void {
  try {
    localStorage.setItem(PROCESS_FORMAT_INITIAL_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save Process Format initial data', e);
  }
}

const MAX_CYCLES = 100;

const emptyStartForm = { productName: '', executiveName: '', lineNo: '' };
const emptyItemRow: ProcessFormatItemData = {
  itemname: '',
  noofbiscuits: '',
  qtyused: '',
  status: 'Ok',
  variance: '',
};

function idx(cycle: number) {
  return Math.max(0, Math.min(cycle - 1, MAX_CYCLES - 1));
}

const ProcessFormat: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { plantTourId } = useSelector((state: RootState) => state.planTour ?? {});
  const user = useSelector((state: RootState) => state.user?.user);
  const isOfflineStarted = useSelector((state: RootState) => state.appState?.isOfflineStarted ?? false);
  const reduxFetchedCycles = useSelector((state: RootState) => state.processFormat?.fetchedCycles ?? []);

  const offlineInitial = useMemo(() => {
    try {
      const s = store.getState();
      if (!s?.appState?.isOfflineStarted) return null;
      const fetched = s?.processFormat?.fetchedCycles ?? [];
      if (!Array.isArray(fetched) || fetched.length === 0) return null;
      const byCycle: ProcessFormatCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      fetched.forEach((cycleData: ProcessFormatCycleData) => {
        const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            lineNo: cycleData.lineNo || '',
            tourstartdate: cycleData.tourstartdate || '',
            items: cycleData.items || [],
          });
        }
      });
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((sessions, index) => { if (sessions.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
      return { savedCycles: byCycle, cycleCompleted: completedCycles, currentCycle: maxCycleFromData + 1 };
    } catch {
      return null;
    }
  }, []);

  const [cycleCompleted, setCycleCompleted] = useState<boolean[]>(
    () => offlineInitial?.cycleCompleted ?? Array(MAX_CYCLES).fill(false)
  );
  const [isSessionStarted, setIsSessionStarted] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [cycleExpanded, setCycleExpanded] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [savedCycles, setSavedCycles] = useState<ProcessFormatCycleData[][]>(
    () => offlineInitial?.savedCycles ?? Array(MAX_CYCLES).fill(null).map(() => [])
  );
  const [showStartFormForNewProduct, setShowStartFormForNewProduct] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [formDataByCycle, setFormDataByCycle] = useState<typeof emptyStartForm[]>(
    () => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptyStartForm }))
  );
  const [tourstartdateByCycle, setTourstartdateByCycle] = useState<string[]>(
    () => Array(MAX_CYCLES).fill('').map((_, i) => (i === 0 ? moment().format('MM-DD-YYYY') : ''))
  );
  const [itemRowsByCycle, setItemRowsByCycle] = useState<ProcessFormatItemData[][]>(
    () => Array(MAX_CYCLES).fill(null).map(() => [{ ...emptyItemRow }])
  );
  const [currentCycle, setCurrentCycle] = useState(() => offlineInitial?.currentCycle ?? 1);
  const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<number>>(new Set());

  const setCycleCompletedAt = (cycle: number, value: boolean) =>
    setCycleCompleted((prev) => {
      const n = [...prev];
      n[idx(cycle)] = value;
      return n;
    });
  const setIsSessionStartedAt = (cycle: number, value: boolean) =>
    setIsSessionStarted((prev) => {
      const n = [...prev];
      n[idx(cycle)] = value;
      return n;
    });
  const setCycleExpandedAt = (cycle: number, value: boolean) =>
    setCycleExpanded((prev) => {
      const n = [...prev];
      n[idx(cycle)] = value;
      return n;
    });
  const setSavedCyclesAt = (cycle: number, updater: (prev: ProcessFormatCycleData[]) => ProcessFormatCycleData[]) =>
    setSavedCycles((prev) => {
      const n = prev.map((a) => [...a]);
      n[idx(cycle)] = updater(n[idx(cycle)]);
      return n;
    });
  const setShowStartFormForNewProductAt = (cycle: number, value: boolean) =>
    setShowStartFormForNewProduct((prev) => {
      const n = [...prev];
      n[idx(cycle)] = value;
      return n;
    });
  const setFormDataByCycleAt = (cycle: number, value: typeof emptyStartForm) =>
    setFormDataByCycle((prev) => {
      const n = prev.map((x) => ({ ...x }));
      n[idx(cycle)] = value;
      return n;
    });
  const setTourstartdateAt = (cycle: number, value: string) =>
    setTourstartdateByCycle((prev) => {
      const n = [...prev];
      n[idx(cycle)] = value;
      return n;
    });
  const setItemRowsByCycleAt = (cycle: number, updater: (prev: ProcessFormatItemData[]) => ProcessFormatItemData[]) =>
    setItemRowsByCycle((prev) => {
      const n = prev.map((x) => [...x]);
      n[idx(cycle)] = updater(n[idx(cycle)]);
      return n;
    });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    cycle: number,
    isItemRow: boolean,
    itemIndex?: number
  ) => {
    const { name, value } = e.target;
    const i = idx(cycle);
    if (isItemRow && itemIndex !== undefined) {
      setItemRowsByCycleAt(cycle, (prev) => {
        const next = prev.map((row, idx) =>
          idx === itemIndex ? { ...row, [name]: value } : row
        );
        return next;
      });
    } else {
      setFormDataByCycle((prev) => {
        const n = prev.map((x) => ({ ...x }));
        n[i] = { ...n[i], [name]: value };
        return n;
      });
    }
  };

  const handleStartSession = (cycle: number = 1) => {
    const data = formDataByCycle[idx(cycle)];
    const dateVal = tourstartdateByCycle[idx(cycle)] || moment().format('MM-DD-YYYY');
    saveProcessFormatInitial({
      productName: data.productName || '',
      executiveName: data.executiveName || '',
      lineNo: data.lineNo || '',
    });
    setTourstartdateAt(cycle, dateVal);
    setIsSessionStartedAt(cycle, true);
  };

  const handleCancelSession = (cycle: number = 1) => {
    setIsSessionStartedAt(cycle, false);
  };

  const handleSaveSession = async (cycle: number = 1) => {
    if (!plantTourId) {
      alert('Plant Tour ID is missing. Please start a plant tour first.');
      return;
    }
    const i = idx(cycle);
    const startData = formDataByCycle[i];
    const items = itemRowsByCycle[i] || [];
    const productName = (startData.productName || '').trim() || 'N/A';
    const executiveName = (startData.executiveName || '').trim() || 'N/A';
    const lineNo = (startData.lineNo || '').trim() || null;
    const tourstartdate = tourstartdateByCycle[i] || moment().format('MM-DD-YYYY');
    const userName = user?.Name || null;

    const records = items.map((item) =>
      collectProcessFormatRecord(
        cycle,
        productName,
        executiveName,
        lineNo,
        (item.itemname || '').trim() || null,
        null,
        item.qtyused ? parseInt(String(item.qtyused), 10) : null,
        (item.status || '').trim() || null,
        (item.variance || '').trim() || null,
        plantTourId,
        userName
      )
    );

    const savedSession: ProcessFormatCycleData = {
      cycleNum: String(cycle),
      product: productName,
      executiveName,
      lineNo: lineNo || '',
      tourstartdate,
      items: items.map((it) => ({
        itemname: it.itemname || '',
        noofbiscuits: it.noofbiscuits || '',
        qtyused: it.qtyused || '',
        status: it.status || 'Ok',
        variance: it.variance || '',
      })),
    };

    try {
      if (isOfflineStarted) {
        dispatch(
          addOfflineData({
            cycleNo: cycle,
            records: [savedSession],
            timestamp: Date.now(),
          })
        );
        setSavedCyclesAt(cycle, (prev) => [...prev, savedSession]);
        setIsSessionStartedAt(cycle, false);
        setShowStartFormForNewProductAt(cycle, false);
        try {
          const key = `${PROCESS_FORMAT_CYCLE_DATA_KEY}-${cycle}-data`;
          const existing = localStorage.getItem(key);
          const existingArr = existing ? (JSON.parse(existing) as any[]) : [];
          const merged = Array.isArray(existingArr) ? [...existingArr, ...records] : records;
          localStorage.setItem(key, JSON.stringify(merged));
        } catch (_) {}
        showOfflineSaveAlertForCategory(cycle, 'Process Format');
      } else {
        const result = await savesectionApicall(records);
        if (result.success) {
          setSavedCyclesAt(cycle, (prev) => [...prev, savedSession]);
          setIsSessionStartedAt(cycle, false);
          setShowStartFormForNewProductAt(cycle, false);
        } else {
          alert(`Failed to save Cycle ${cycle}: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save session.'}`);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const handleCompleteCycle = (cycle: number) => {
    const sessions = savedCycles[idx(cycle)];
    if (!sessions || sessions.length === 0) {
      alert('Please save at least one product before completing the cycle.');
      return;
    }
    setCycleCompletedAt(cycle, true);
    setIsSessionStartedAt(cycle, false);
    setShowStartFormForNewProductAt(cycle, false);
    if (cycle === currentCycle) setCurrentCycle((prev) => prev + 1);
  };

  const handleAddNewProduct = (cycle: number) => {
    const initial = getProcessFormatInitial();
    setShowStartFormForNewProductAt(cycle, true);
    setFormDataByCycleAt(cycle, {
      productName: initial?.productName ?? '',
      executiveName: initial?.executiveName ?? '',
      lineNo: initial?.lineNo ?? '',
    });
    setItemRowsByCycleAt(cycle, () => [{ ...emptyItemRow }]);
    setTourstartdateAt(cycle, moment().format('MM-DD-YYYY'));
  };

  const toggleCompletedCycleExpansion = (cycleNum: number) => {
    setExpandedCompletedCycles((prev) => {
      const next = new Set(prev);
      if (next.has(cycleNum)) next.delete(cycleNum);
      else next.add(cycleNum);
      return next;
    });
  };

  // When offline (isOfflineStarted): no API calls; data comes from Redux (fetchedCycles) and localStorage only.
  const loadCycleData = async () => {
    if (!plantTourId || isOfflineStarted) return;
    try {
      const cycles = await fetchCycleData(plantTourId);
      const mapped: ProcessFormatCycleData[] = cycles.map((c: FetchedCycleData) => ({
        cycleNum: c.cycleNum,
        product: c.product,
        executiveName: c.executiveName,
        lineNo: c.lineNo,
        tourstartdate: c.tourstartdate,
        items: c.items || [],
      }));
      dispatch(setFetchedCycles(mapped));
      const byCycle: ProcessFormatCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cycles.forEach((cycleData: FetchedCycleData) => {
        const cycleNum = parseInt(cycleData.cycleNum, 10);
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: cycleData.cycleNum,
            product: cycleData.product,
            executiveName: cycleData.executiveName,
            lineNo: cycleData.lineNo,
            tourstartdate: cycleData.tourstartdate,
            items: cycleData.items || [],
          });
        }
      });
      setSavedCycles(byCycle);
      setCycleCompleted((prev) => prev.map((v, i) => (byCycle[i].length > 0 ? true : v)));
      const maxCycleFromData = cycles.length
        ? Math.max(...cycles.map((cd) => parseInt(String(cd.cycleNum), 10) || 0), 1)
        : 0;
      setCurrentCycle(maxCycleFromData + 1);
    } catch (error) {
      console.error('Error loading cycle data:', error);
    }
  };

  useLayoutEffect(() => {
    if (!plantTourId || !isOfflineStarted) return;
    const fetched = store.getState()?.processFormat?.fetchedCycles ?? [];
    if (!Array.isArray(fetched) || fetched.length === 0) return;
    const byCycle: ProcessFormatCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
    fetched.forEach((cycleData: ProcessFormatCycleData) => {
      const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
      const cycleNum = parseInt(cycleNumStr, 10);
      if (isNaN(cycleNum) || cycleNum < 1) return;
      const i = idx(cycleNum);
      if (i >= 0 && i < MAX_CYCLES) {
        byCycle[i].push({
          cycleNum: String(cycleNum),
          product: cycleData.product || '',
          executiveName: cycleData.executiveName || '',
          lineNo: cycleData.lineNo || '',
          tourstartdate: cycleData.tourstartdate || '',
          items: cycleData.items || [],
        });
      }
    });
    setSavedCycles(byCycle);
    setCycleCompleted(byCycle.map((sessions) => sessions.length > 0));
    const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
    setCurrentCycle(maxCycleFromData + 1);
  }, [plantTourId, isOfflineStarted]);

  useEffect(() => {
    if (!plantTourId) return;
    if (isOfflineStarted) {
      const cyclesToUse = store.getState()?.processFormat?.fetchedCycles ?? [];
      if (!Array.isArray(cyclesToUse) || cyclesToUse.length === 0) {
        setSavedCycles(Array(MAX_CYCLES).fill(null).map(() => []));
        setCycleCompleted(Array(MAX_CYCLES).fill(false));
        setCurrentCycle(1);
        return;
      }
      const byCycle: ProcessFormatCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cyclesToUse.forEach((cycleData: ProcessFormatCycleData) => {
        const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            lineNo: cycleData.lineNo || '',
            tourstartdate: cycleData.tourstartdate || '',
            items: cycleData.items || [],
          });
        }
      });
      setSavedCycles(byCycle);
      setCycleCompleted(byCycle.map((sessions) => sessions.length > 0));
      const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
      setCurrentCycle(maxCycleFromData + 1);
      return;
    }
    loadCycleData();
  }, [plantTourId, isOfflineStarted, reduxFetchedCycles?.length]);

  useEffect(() => {
    const initial = getProcessFormatInitial();
    if (!initial?.productName && !initial?.executiveName && !initial?.lineNo) return;
    setFormDataByCycle((prev) =>
      prev.map((p, i) => {
        const cycleNum = i + 1;
        if (cycleNum === 1) {
          if (p.productName || p.executiveName || p.lineNo) return p;
          return {
            productName: initial?.productName ?? '',
            executiveName: initial?.executiveName ?? '',
            lineNo: initial?.lineNo ?? '',
          };
        }
        if (!cycleCompleted[cycleNum - 2]) return p;
        if (p.productName || p.executiveName || p.lineNo) return p;
        return {
          productName: initial?.productName ?? '',
          executiveName: initial?.executiveName ?? '',
          lineNo: initial?.lineNo ?? '',
        };
      })
    );
  }, [cycleCompleted]);

  const headerDate = moment().format('DD-MM-YYYY');
  const shiftValue =
    typeof localStorage !== 'undefined' ? localStorage.getItem('shiftValue') || 'Shift 1' : 'Shift 1';

  const renderSummaryTable = (session: ProcessFormatCycleData) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Item Name</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Qty Used</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Status</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Actual Variance</th>
          </tr>
        </thead>
        <tbody>
          {(session.items || []).map((item, index) => (
            <tr key={index}>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{item.itemname || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{item.qtyused || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{item.status || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{item.variance || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="w-full px-4 md:px-6 py-6">
        <div className="mb-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 text-gray-800 hover:text-gray-600 font-semibold bg-transparent border-0 cursor-pointer p-0"
          >
            <span className="text-lg">←</span>
            <span>Back</span>
          </button>
        </div>
        <div className="flex items-center justify-between px-4 py-3 mb-6 rounded-lg bg-gray-100 border border-gray-200">
          <h1 className="text-xl font-bold text-gray-800">Process Format</h1>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-200 text-gray-800 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
              {shiftValue}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-sm text-gray-800">{headerDate}</span>
            <button type="button" className="p-1 rounded hover:bg-gray-200 text-gray-600" aria-label="More options">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 space-y-6">
          {/* Completed cycles */}
          {Array.from({ length: MAX_CYCLES }, (_, i) => i + 1)
            .filter((n) => cycleCompleted[idx(n)] || savedCycles[idx(n)].length > 0)
            .map((n) => (
              <div key={n} className="light-surface bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer border-b border-gray-200"
                  onClick={() => toggleCompletedCycleExpansion(n)}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {n}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${expandedCompletedCycles.has(n) ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {expandedCompletedCycles.has(n) && savedCycles[idx(n)].length > 0 && (
                  <div className="p-4 space-y-6">
                    {savedCycles[idx(n)].map((session, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Product: </span>
                              <span className="font-semibold text-gray-800">{session.product || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Executive Name: </span>
                              <span className="font-semibold text-gray-800">{session.executiveName || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Line No: </span>
                              <span className="font-semibold text-gray-800">{session.lineNo || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Date: </span>
                              <span className="font-semibold text-gray-800">{session.tourstartdate || '-'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-t-lg px-4 py-2">
                          <h3 className="text-sm font-bold text-red-700">Summary</h3>
                        </div>
                        <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">
                          {renderSummaryTable(session)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* Current cycle card */}
          <div className="light-surface bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {!cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && (
              <CycleHeader cycleNumber={currentCycle} />
            )}

            {/* Start section: show when no products yet or user clicked "Add new product" */}
            {!isSessionStarted[idx(currentCycle)] &&
              !cycleCompleted[idx(currentCycle)] &&
              (savedCycles[idx(currentCycle)].length === 0 || showStartFormForNewProduct[idx(currentCycle)]) && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                      <input
                        type="text"
                        name="productName"
                        value={formDataByCycle[idx(currentCycle)].productName}
                        onChange={(e) => handleInputChange(e, currentCycle, false)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter value"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Executive Name</label>
                      <input
                        type="text"
                        name="executiveName"
                        value={formDataByCycle[idx(currentCycle)].executiveName}
                        onChange={(e) => handleInputChange(e, currentCycle, false)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter value"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Line No</label>
                      <input
                        type="text"
                        name="lineNo"
                        value={formDataByCycle[idx(currentCycle)].lineNo}
                        onChange={(e) => handleInputChange(e, currentCycle, false)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter value"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input
                        type="text"
                        name="tourstartdate"
                        value={tourstartdateByCycle[idx(currentCycle)] || moment().format('MM-DD-YYYY')}
                        onChange={(e) => setTourstartdateAt(currentCycle, e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="MM-DD-YYYY"
                      />
                    </div>
                  </div>
                  <StartSessionButton onClick={() => handleStartSession(currentCycle)} />
                </div>
              )}

            {/* Completed current cycle - show summary */}
            {cycleCompleted[idx(currentCycle)] && (
              <>
                <CycleCompletedHeader
                  cycleNumber={currentCycle}
                  expanded={cycleExpanded[idx(currentCycle)]}
                  onToggle={() => setCycleExpandedAt(currentCycle, !cycleExpanded[idx(currentCycle)])}
                />
                {cycleExpanded[idx(currentCycle)] && (
                  <div className="space-y-6">
                    {savedCycles[idx(currentCycle)].map((session, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Product: </span>
                              <span className="font-semibold text-gray-800">{session.product || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Executive Name: </span>
                              <span className="font-semibold text-gray-800">{session.executiveName || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Line No: </span>
                              <span className="font-semibold text-gray-800">{session.lineNo || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Date: </span>
                              <span className="font-semibold text-gray-800">{session.tourstartdate || '-'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-t-lg px-4 py-2">
                          <h3 className="text-sm font-bold text-red-700">Summary</h3>
                        </div>
                        <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">
                          {renderSummaryTable(session)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* After save: Add new product / Complete Cycle (multiple products per cycle) */}
            {savedCycles[idx(currentCycle)].length > 0 &&
              !cycleCompleted[idx(currentCycle)] &&
              !isSessionStarted[idx(currentCycle)] &&
              !showStartFormForNewProduct[idx(currentCycle)] && (
              <CycleSavedBanner
                cycleNumber={currentCycle}
                savedCount={savedCycles[idx(currentCycle)].length}
              >
                    <button
                      type="button"
                      onClick={() => handleAddNewProduct(currentCycle)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                    >
                      Add new product
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCompleteCycle(currentCycle)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                    >
                      Complete Cycle & Move to Next
                    </button>
              </CycleSavedBanner>
            )}

            {/* Save section: info bar + Items + Cancel / Save Session */}
            {isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Product</div>
                      <div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].productName || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                      <div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].executiveName || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Line No</div>
                      <div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].lineNo || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Date</div>
                      <div className="font-semibold text-gray-800">{tourstartdateByCycle[idx(currentCycle)] || '-'}</div>
                    </div>
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-800 mb-4">Items</h3>
                <div className="space-y-4 mb-6">
                  {(itemRowsByCycle[idx(currentCycle)] || []).map((row, itemIndex) => (
                    <div key={itemIndex} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Item Name</label>
                        <input
                          type="text"
                          name="itemname"
                          value={row.itemname}
                          onChange={(e) => handleInputChange(e, currentCycle, true, itemIndex)}
                          className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter value"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Qty Used</label>
                        <input
                          type="text"
                          name="qtyused"
                          value={row.qtyused}
                          onChange={(e) => handleInputChange(e, currentCycle, true, itemIndex)}
                          className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter value"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Status (Ok/Not Okay)</label>
                        <select
                          name="status"
                          value={row.status || 'Ok'}
                          onChange={(e) => handleInputChange(e, currentCycle, true, itemIndex)}
                          className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="Ok">Ok</option>
                          <option value="Not Okay">Not Okay</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Actual Variance</label>
                        <input
                          type="text"
                          name="variance"
                          value={row.variance}
                          onChange={(e) => handleInputChange(e, currentCycle, true, itemIndex)}
                          className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter variance"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-4">
                    <button
                      onClick={() => handleCancelSession(currentCycle)}
                      className="px-6 py-2 border border-blue-300 text-blue-600 bg-white rounded-md hover:bg-blue-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSaveSession(currentCycle)}
                      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                    >
                      Save Session
                    </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProcessFormat;
